import { LoaderCircle } from "lucide-react";
import type { FunctionComponent, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "./cn";

const SIZE_CLASSES = { small: "size-3.5", default: "size-5", large: "size-8" } as const;

export interface SpinProps {
  /** Default true. */
  spinning?: boolean;
  size?: keyof typeof SIZE_CLASSES;
  /** Text under the indicator. */
  tip?: ReactNode;
  /** With children, overlays them (dimmed, non-interactive) while spinning. */
  children?: ReactNode;
  className?: string;
}

/** Loading indicator (antd `Spin` replacement). */
export const Spin: FunctionComponent<SpinProps> = ({ spinning = true, size = "default", tip, children, className }) => {
  const { t } = useTranslation();

  const indicator = (
    <span role="status" aria-live="polite" className="inline-flex flex-col items-center gap-2 text-noa-primary">
      <LoaderCircle className={cn("animate-spin", SIZE_CLASSES[size])} aria-hidden />
      {tip ? (
        <span className="text-sm">{tip}</span>
      ) : (
        <span className="sr-only">{t("common:asyncStatus.loading")}</span>
      )}
    </span>
  );

  if (children === undefined) {
    return spinning ? <span className={cn("inline-flex", className)}>{indicator}</span> : null;
  }

  return (
    <div className={cn("relative", className)} aria-busy={spinning}>
      <div
        inert={spinning}
        className={cn("transition-opacity", spinning && "pointer-events-none opacity-50 select-none")}
      >
        {children}
      </div>
      {spinning && <div className="absolute inset-0 z-10 flex items-center justify-center">{indicator}</div>}
    </div>
  );
};
